import { useEffect, useId, useState } from 'react'
import type { CompactPlan, SinglePlan } from 'prove-common'
import {
  CheckIcon,
  ModalDialog,
  Mtds,
  useCopyPlanMutation,
  useAuth,
  useGetPlanByIdQuery,
  SaveError,
  XIcon
} from 'prove-common'
import { useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'

type CopyPlanModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  plan: SinglePlan | CompactPlan
}

export const CopyPlanModal = ({
  isOpen,
  setOpen,
  plan
}: CopyPlanModalProps) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [newPlanName, setNewPlanName] = useState('')
  const [isCopying, setIsCopying] = useState(false)
  const copyPlan = useCopyPlanMutation()
  const { isAdmin, permissions = [] } = useAuth()
  const detail = useGetPlanByIdQuery(isOpen ? plan.id : undefined)
  const parentId = detail.data?.parents?.[0]?.id
  const destination = useGetPlanByIdQuery(isOpen ? parentId : undefined)
  const canCopy =
    detail.isSuccess &&
    !detail.isFetching &&
    (parentId == null
      ? isAdmin ||
        permissions.some(
          (permission) => permission.responsibility === 'PROGRAM_MANAGER'
        )
      : destination.data?.canCreateChildren === true && !destination.isFetching)
  const [saveError, setSaveError] = useState<unknown>()
  const id = useId()
  const modalId = `copy-plan-modal-${id}`
  useEffect(
    () => setNewPlanName((plan?.description ?? '') + ' (kopi)'),
    [plan.description]
  )

  return (
    <ModalDialog
      id={modalId}
      closedby='any'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button command='close' commandfor={modalId} aria-label={'Lukk'} />
      <Mtds.Prose>
        <Mtds.Heading>Gi planen et navn</Mtds.Heading>
        <SaveError error={saveError} action='kopiere planen' />

        {isCopying ? (
          <Mtds.Grid data-justify='center'>
            <Mtds.Spinner data-size='lg' />
            <p>Dupliserer...</p>
            <h5>Den nye planen vil være klar om få strakser</h5>
          </Mtds.Grid>
        ) : (
          <>
            <p>
              Velg et navn som gjør det enkelt å finne igjen planen senere. En
              duplisert plan vil først lagres som utkast og kan publiseres
              senere.
            </p>
            <Mtds.Field
              as='input'
              label='Navn på plan'
              value={newPlanName}
              onChange={({ target }) => setNewPlanName(target.value)}
            />
            <Mtds.Flex>
              <Mtds.Button command='close' commandfor={modalId}>
                <XIcon />
                Avbryt
              </Mtds.Button>
              <Mtds.Button
                data-variant='primary'
                disabled={!canCopy}
                onClick={() => {
                  if (plan?.ownerId == null)
                    return Mtds.toast.danger('Planen har ingen eier')

                  setIsCopying(true)
                  setSaveError(undefined)
                  const copyPlanPromise = copyPlan.mutateAsync({
                    planId: plan.id,
                    body: {
                      ownerId: 'self',
                      newDescription: newPlanName
                    }
                  })

                  const delayPromise = new Promise((resolve) =>
                    setTimeout(resolve, 3000)
                  )

                  Promise.all([copyPlanPromise, delayPromise])
                    .then(async ([copiedPlan]) => {
                      navigate(`/plans/${copiedPlan.id}`)
                      await queryClient.invalidateQueries({
                        queryKey: ['plans']
                      })
                      setOpen(false)
                    })
                    .catch((error) => {
                      setSaveError(error)
                    })
                    .finally(() => setIsCopying(false))
                }}
              >
                Lagre utkast
                <CheckIcon />
              </Mtds.Button>
            </Mtds.Flex>
          </>
        )}
      </Mtds.Prose>
    </ModalDialog>
  )
}
